/* eslint-disable complexity */
import React from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { Link } from 'react-router-dom';

import SafelyRenderHtml from '@src/services/renderEditorText';

import { Agent360Keyword } from '@src/components/global/agent360Keyword';
import { ArsKeyword } from '@src/components/global/arsKeyword';
import { isArs } from '@src/components/helpers';

class HeaderMenuBottomPanel extends React.Component {
    static propTypes = {
        menu: PropTypes.shape({
            id: PropTypes.string.isRequired,
            panels: PropTypes.arrayOf(PropTypes.shape({
                title: PropTypes.string,
                description: PropTypes.string,
                listOfLinks: PropTypes.arrayOf(PropTypes.shape({
                    url: PropTypes.string.isRequired,
                    displayName: PropTypes.string,
                    // eslint-disable-next-line camelcase
                    is_new: PropTypes.bool,
                })),
                // eslint-disable-next-line camelcase
                access_level: PropTypes.string,
                listOfDescribedLinks: PropTypes.arrayOf(PropTypes.shape({
                    description: PropTypes.string.isRequired,
                    url: PropTypes.string.isRequired,
                    displayName: PropTypes.string,
                })),
            })).isRequired,
        }).isRequired,
        hoverMenuId: PropTypes.string,
        menuTabIndex: PropTypes.number,
        onClick: PropTypes.func.isRequired,
        onMouseLeave: PropTypes.func.isRequired,
        onFocus: PropTypes.func.isRequired,
        onBlur: PropTypes.func.isRequired,
    };

    renderLink = (panelLink, j, k) => {
        const { onClick, menuTabIndex } = this.props;

        if (panelLink.url.search(/^https?:\/\//) !== -1) {
            return (
                <li key={`panel-li-${j}-${k}`}>
                    <a
                        href={panelLink.url}
                        className="external-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        tabIndex={menuTabIndex}
                        onClick={onClick}
                    >
                        {SafelyRenderHtml(panelLink.displayName)}
                        {panelLink.platform === 'agent360' && <Agent360Keyword />}
                        {panelLink.platform !== 'agent360' && isArs(panelLink.displayName) && <ArsKeyword title={panelLink.displayName} />}
                    </a>
                </li>
            );
        }

        return (
            <li key={`panel-li-${j}-${k}`}>
                <Link to={panelLink.url} tabIndex={menuTabIndex} onClick={onClick}>
                    {SafelyRenderHtml(panelLink.displayName)}
                    {panelLink.platform === 'agent360' && <Agent360Keyword />}
                    {panelLink.platform !== 'agent360' && isArs(panelLink.displayName) && <ArsKeyword title={panelLink.displayName} />}
                </Link>
            </li>
        );
    };

    renderDescribedLink = (panelDescribedLink, j, k) => {
        const { onClick, onBlur, menuTabIndex } = this.props;

        if (panelDescribedLink.url.search(/^https?:\/\//) !== -1) {
            return (
                <section key={`panel-li-${j}-${k}`}>
                    <a
                        href={panelDescribedLink.url}
                        className="external-link font-weight-bold"
                        target="_blank"
                        rel="noopener noreferrer"
                        tabIndex={menuTabIndex}
                        onClick={onClick}
                        onBlur={onBlur}
                    >
                        {SafelyRenderHtml(panelDescribedLink.displayName)}
                        {panelDescribedLink.platform === 'agent360' && <Agent360Keyword />}
                        {panelDescribedLink.platform !== 'agent360' && isArs(panelDescribedLink.displayName) && <ArsKeyword title={panelDescribedLink.displayName} />}
                    </a>
                    <p>
                        {SafelyRenderHtml(panelDescribedLink.description)}
                    </p>
                </section>
            );
        }

        return (
            <section key={`panel-section-${j}-${k}`}>
                <Link to={panelDescribedLink.url} className="font-weight-bold" tabIndex={menuTabIndex} onClick={onClick} onBlur={onBlur}>
                    {SafelyRenderHtml(panelDescribedLink.displayName)}
                    {panelDescribedLink.platform === 'agent360' && <Agent360Keyword />}
                    {panelDescribedLink.platform !== 'agent360' && isArs(panelDescribedLink.displayName) && <ArsKeyword title={panelDescribedLink} />}
                </Link>
                <p>
                    {SafelyRenderHtml(panelDescribedLink.description)}
                </p>
            </section>
        );
    };

    renderPanel = (panel, j) => {
        const { menu } = this.props;
        const { onClick, menuTabIndex } = this.props;

        const numberOfPanels = menu.panels.length;
        const fourPanels = numberOfPanels === 4;
        const threePanels = numberOfPanels === 3;
        const twoPanels = numberOfPanels === 2;
        const agent360Panels = menu.id === 'agent360';
        const productPanels = menu.id === 'products';
        const salesPanels = menu.id === 'sales_and_marketing';

        function colSize() {
            if (twoPanels) {
                return 6;
            }

            if (productPanels) {
                switch (j) {
                case 0:
                    return null;
                case 1:
                    return null;
                case 2:
                    return 3;
                case 3:
                    return 4;
                default:
                    return null;
                }
            }

            if (salesPanels) { // Sales panel and only 1 panel
                return 12;
            }

            if (fourPanels) {
                return 3;
            }

            return 4;
        }

        const classNames = classnames({
            'three-column-panel': (threePanels && j === 1),
            'two-column-panel': (twoPanels && j === 1) || (salesPanels && j === 0),
        });

        const columnsClassNames = classnames({
            'two-column-count': (threePanels && agent360Panels && j === 1),
        });

        if (panel.title === 'Agent360 Product Guide') {
            const panelLink = panel.listOfLinks[0];

            return (
                <Col
                    key={panel.title}
                    id="agent360-guide-panel"
                    xs={colSize()}
                    className={classNames}
                >
                    <div id="agent360-guide-download" className="pr-3">
                        <img
                            src="/img/agent360-product-guide-preview.png"
                            alt="Agent360 Product Guide"
                            className="guide-img"
                        />
                        <ul>
                            <li key="panel-li-agent360-download">
                                <Link
                                    to={panelLink.url}
                                    tabIndex={menuTabIndex}
                                    className="ml-button-primary download-guide px-0"
                                    onClick={onClick}
                                >
                                    {SafelyRenderHtml(panelLink.displayName)}
                                    {panelLink.platform === 'agent360' && <Agent360Keyword />}
                                    {panelLink.platform !== 'agent360' && isArs(panelLink.displayName) && <ArsKeyword title={panelLink.displayName} />}
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div id="agent360-guide-description">
                        <h1 className="py-3">{SafelyRenderHtml(panel.title)}</h1>
                        <hr />
                        {panel.description && <p>{SafelyRenderHtml(panel.description)}</p>}
                        <ul className={columnsClassNames}>
                            {
                                panel.listOfLinks
                                    && panel.listOfLinks.map((link, k) => this.renderLink(link, j, k))
                            }
                        </ul>
                    </div>
                </Col>
            );
        }

        return (
            <Col
                key={panel.title}
                xs={colSize()}
                className={classNames}
            >
                <h1 className="py-3 text-left">{SafelyRenderHtml(panel.title)}</h1>
                <hr />
                {panel.description && <p>{SafelyRenderHtml(panel.description)}</p>}
                <ul className={columnsClassNames}>
                    {
                        panel.listOfLinks
                            && panel.listOfLinks.map((link, k) => this.renderLink(link, j, k))
                    }
                </ul>
                <section>
                    {
                        panel.listOfDescribedLinks
                            && panel.listOfDescribedLinks.map((link, k) => this.renderDescribedLink(link, j, k))
                    }
                </section>

            </Col>
        );
    };

    render() {
        const {
            menu,
            hoverMenuId,
            onMouseLeave,
            onFocus,
        } = this.props;

        if (hoverMenuId !== menu.id || menu.id === 'guides_and_manuals') {
            return null;
        }

        return (
            <Col
                key={`menu-col-${menu.id}`}
                menuindex={menu.id}
                className="header-bottom-expanded-menu shadow"
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
            >
                <Row className="pb-4 pt-3 pl-2 pr-2">
                    {menu.panels.map((panel, j) => this.renderPanel(panel, j))}
                </Row>
            </Col>
        );
    }
}

export default HeaderMenuBottomPanel;
